import {
  ArrowRight, Crown, Gem, Sparkles,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { PackPromotionBadge } from "@/features/subscription-request/components/PackPromotionBadge";
import type { ActivePromotion, UpsellPlanPricing } from "@/features/subscription-request/types/subscriptionRequest.types";
import { cn } from "@/shared/lib/utils";
import type { PlanFeatures } from "@/shared/types/planFeatures";
import {
  filterAvailableUpgradeFeatures,
  START_PLAN_FEATURES,
  STANDARD_PLAN_FEATURES,
  PREMIUM_PLAN_FEATURES,
} from "../configs/upgradeFeatures.config";
import {
  COLOR_BORDER_SUBTLE,
  COLOR_CHARCOAL,
  COLOR_MUTED,
  COLOR_PURPLE,
  COLOR_PURPLE_DEEP,
  COLOR_SOFT,
  COLOR_SURFACE,
  COLOR_SURFACE_ALT,
  COLOR_TEAL,
  SHADOW_CARD,
} from "../constants/upsellPage.constants";
import PlanPriceBlock from "./PlanPriceBlock";
import Pill from "./primitives/Pill";
import type { UpgradeSectionProps, UpgradeFeatureItem } from "../types/upsellPage.types";

type SubscribePlanCode = "standard" | "premium";

const SubFeatureRow = ({
  label,
  index,
  baseDelay,
  inView,
}: {
  label: string
  index: number
  baseDelay: number
  inView: boolean
}) => (
  <li
    className={cn(
      "flex items-center py-1 pl-8",
      inView ? "animate-upsell-fade-in-up" : "opacity-0",
    )}
    style={inView ? { animationDelay: `${baseDelay + index * 0.04}s` } : undefined}
  >
    <span
      className="mr-2.5 inline-block size-1 shrink-0 rounded-full"
      style={{ backgroundColor: COLOR_SOFT }}
      aria-hidden="true"
    />
    <span className="text-xs" style={{ color: COLOR_SOFT }}>{label}</span>
  </li>
);

const FeatureRow = ({
  feature,
  index,
  baseDelay,
  inView,
  isPremiumExclusive = false,
}: {
  feature: UpgradeFeatureItem
  index: number
  baseDelay: number
  inView: boolean
  isPremiumExclusive?: boolean
}) => {
  const IconComponent = feature.icon;
  const parentDelay = baseDelay + index * 0.04;
  const hasSubFeatures = feature.subFeatures && feature.subFeatures.length > 0;

  return (
    <>
      <li
        className={cn(
          "flex items-center gap-3 py-1.5",
          inView ? "animate-upsell-fade-in-up" : "opacity-0",
        )}
        style={inView ? { animationDelay: `${parentDelay}s` } : undefined}
      >
        {IconComponent && (
          <IconComponent
            size={15}
            strokeWidth={1.75}
            className="shrink-0"
            style={{ color: isPremiumExclusive ? COLOR_TEAL : COLOR_PURPLE }}
            aria-hidden="true"
          />
        )}
        <span
          className={cn("text-sm", isPremiumExclusive ? "font-medium" : "font-normal")}
          style={{ color: COLOR_CHARCOAL }}
        >
          {feature.label}
        </span>
      </li>
      {hasSubFeatures && feature.subFeatures!.map((sub, subIndex) => (
        <SubFeatureRow
          key={sub.label}
          label={sub.label}
          index={subIndex}
          baseDelay={parentDelay + 0.02}
          inView={inView}
        />
      ))}
    </>
  );
};

type ExtendedProps = UpgradeSectionProps & {
  features?: Partial<PlanFeatures>
  promotionsByCode?: Partial<Record<SubscribePlanCode, ActivePromotion | null>>
  pricingsByCode?: Partial<Record<SubscribePlanCode, UpsellPlanPricing | undefined>>
  onSubscribe?: (planCode: SubscribePlanCode) => void
};

const UpgradeSection = ({
  currentPlanName, promotionsByCode, pricingsByCode, onSubscribe, features,
}: ExtendedProps) => {
  const startFeatures = filterAvailableUpgradeFeatures(START_PLAN_FEATURES, features);
  const standardFeatures = filterAvailableUpgradeFeatures(STANDARD_PLAN_FEATURES, features);
  const premiumFeatures = filterAvailableUpgradeFeatures(PREMIUM_PLAN_FEATURES, features);
  const sectionRef = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);
  const standardPromotion = promotionsByCode?.standard ?? null;
  const premiumPromotion = promotionsByCode?.premium ?? null;
  const standardPricing = pricingsByCode?.standard;
  const premiumPricing = pricingsByCode?.premium;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const ArrowChip = () => (
    <div className="flex flex-col items-center justify-center gap-2 self-center py-8">
      <ArrowRight size={20} strokeWidth={1.5} style={{ color: COLOR_SOFT }} aria-hidden="true" />
    </div>
  );

  return (
    <section
      id="upgrade-section"
      ref={sectionRef}
      aria-labelledby="upgrade-heading"
      className={cn(
        "mx-auto w-full max-w-7xl px-4 py-16 sm:py-24",
        isInView ? "animate-upsell-fade-in-up" : "opacity-0",
      )}
      style={isInView ? { animationDelay: "0.1s" } : undefined}
    >
      {/* Heading */}
      <div className="mb-14 flex flex-col items-center text-center">
        <Pill
          variant="tinted-purple"
          size="sm"
          className="mb-5"
        >
          Comparer les plans
        </Pill>
        <h2
          id="upgrade-heading"
          className="font-light tracking-tight"
          style={{
            color: COLOR_CHARCOAL,
            fontSize: "clamp(2rem, 4vw, 3rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          Prêt à passer au niveau supérieur ?
        </h2>
        <p
          className="mt-4 max-w-xl text-base sm:text-lg"
          style={{ color: COLOR_MUTED, lineHeight: 1.55 }}
        >
          Comparez votre plan actuel et découvrez tout ce que nos packs peuvent vous offrir.
        </p>
      </div>

      {/* ───── Desktop: 5 columns (3 plans + 2 arrows) ───── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-start lg:gap-4">
        {/* Column A: Start Plan */}
        <div
          className="rounded-3xl p-7"
          style={{
            background: COLOR_SURFACE,
            border: `1px solid ${COLOR_BORDER_SUBTLE}`,
          }}
        >
          <div className="mb-5 flex items-center justify-between gap-2">
            <Pill variant="bordered" size="sm">
              Plan actuel
            </Pill>
            <span className="text-xs font-medium" style={{ color: COLOR_SOFT }}>{currentPlanName}</span>
          </div>
          <p className="mb-5 text-xs" style={{ color: COLOR_SOFT }}>
            {startFeatures.length}
            {" "}
            fonctionnalité
            {startFeatures.length > 1 ? "s" : ""}
            {" "}
            active
            {startFeatures.length > 1 ? "s" : ""}
          </p>
          <ul className="flex flex-col" role="list">
            {startFeatures.map((feature, index) => (
              <FeatureRow
                key={feature.featureKey ?? feature.label}
                feature={feature}
                index={index}
                baseDelay={0.2}
                inView={isInView}
              />
            ))}
          </ul>
        </div>

        {/* Arrow 1: Start → Standard (hidden on mobile, layout grid handles wrap) */}
        <div className="hidden lg:block">
          <ArrowChip />
        </div>

        {/* Column B: Pack Standard */}
        <div
          className="relative rounded-3xl p-7"
          style={{
            background: COLOR_SURFACE,
            border: `1px solid ${COLOR_BORDER_SUBTLE}`,
            boxShadow: SHADOW_CARD,
          }}
        >
          <div className="mb-5 flex items-center justify-between gap-2">
            <Pill
              variant="tinted-purple"
              size="sm"
              icon={<Crown size={12} strokeWidth={2} />}
            >
              Pack Standard
            </Pill>
            {standardPromotion && (
              <PackPromotionBadge
                discountPercent={standardPromotion.discount_percent}
                title={standardPromotion.title}
              />
            )}
          </div>
          <PlanPriceBlock pricing={standardPricing} />
          <p className="mb-5 text-xs" style={{ color: COLOR_MUTED }}>
            {standardFeatures.length}
            {" "}
            fonctionnalité
            {standardFeatures.length > 1 ? "s" : ""}
            {" "}
            incluse
            {standardFeatures.length > 1 ? "s" : ""}
          </p>
          <ul className="flex flex-col" role="list">
            {standardFeatures.map((feature, index) => (
              <FeatureRow
                key={feature.featureKey ?? feature.label}
                feature={feature}
                index={index}
                baseDelay={0.2}
                inView={isInView}
              />
            ))}
          </ul>
          {onSubscribe && (
            <Pill
              as="button"
              variant="bordered"
              size="md"
              type="button"
              onClick={() => onSubscribe("standard")}
              className="mt-6 w-full"
            >
              Souscrire au Pack Standard
            </Pill>
          )}
        </div>

        {/* Arrow 2: Standard → Premium */}
        <div className="hidden lg:block">
          <ArrowChip />
        </div>

        {/* Column C: Pack Premium */}
        <div
          className="relative rounded-3xl p-7"
          style={{
            background: COLOR_SURFACE_ALT,
            border: `1px solid ${COLOR_BORDER_SUBTLE}`,
            boxShadow: SHADOW_CARD,
          }}
        >
          {/* Recommandé tag */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Pill
              size="sm"
              variant="tinted-teal"
              icon={<Sparkles size={12} strokeWidth={2} />}
              className="border border-white shadow-[0_2px_8px_rgba(45,212,191,0.18)]"
            >
              Recommandé
            </Pill>
          </div>

          <div className="mb-5 flex items-center justify-between gap-2">
            <Pill
              variant="tinted-purple"
              size="sm"
              icon={<Gem size={12} strokeWidth={2} />}
            >
              Pack Premium
            </Pill>
            {premiumPromotion && (
              <PackPromotionBadge
                discountPercent={premiumPromotion.discount_percent}
                title={premiumPromotion.title}
              />
            )}
          </div>
          <PlanPriceBlock pricing={premiumPricing} />
          <p className="mb-5 text-xs" style={{ color: COLOR_MUTED }}>
            <span style={{ color: COLOR_PURPLE_DEEP, fontWeight: 500 }}>
              +
              {premiumFeatures.length - standardFeatures.length}
            </span>
            {" "}
            fonctionnalité
            {premiumFeatures.length - standardFeatures.length > 1 ? "s" : ""}
            {" "}
            <span style={{ color: COLOR_TEAL, fontWeight: 500 }}>
              exclusive
              {premiumFeatures.length - standardFeatures.length > 1 ? "s" : ""}
            </span>
            {" "}
            en plus
          </p>
          <ul className="flex flex-col" role="list">
            {premiumFeatures.map((feature, index) => (
              <FeatureRow
                key={feature.featureKey ?? feature.label}
                feature={feature}
                index={index}
                baseDelay={0.2}
                inView={isInView}
                isPremiumExclusive={feature.tier === "premium"}
              />
            ))}
          </ul>
          {onSubscribe && (
            <Pill
              as="button"
              variant="purple"
              size="md"
              type="button"
              onClick={() => onSubscribe("premium")}
              className="mt-6 w-full"
            >
              Souscrire au Pack Premium
              <ArrowRight size={14} strokeWidth={2} />
            </Pill>
          )}
        </div>
      </div>
    </section>
  );
};

export default UpgradeSection;
